import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, RefreshCcw, Target, Shield, Check, ChevronDown, ChevronUp, ExternalLink, Menu, X, Loader2, Link2,
} from 'lucide-react';
import { API_ROOT } from '@/config';
import AuthMobile from './AuthMobile';
import SEO from '@/components/seo';
import { trackEvent } from '@/lib/meta-pixel';

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

/* ================================================================== */
/*  LandingMobile                                                      */
/* ================================================================== */
const LandingMobile: React.FC = () => {
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [openFaqItems, setOpenFaqItems] = useState<Record<number, boolean>>({});

  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    document.body.classList.add('landing-page');
    return () => { document.body.classList.remove('landing-page'); };
  }, []);


  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toggleFaq = (i: number) =>
    setOpenFaqItems(prev => ({ ...prev, [i]: !prev[i] }));

  const openAuth = () => setShowAuthModal(true);

  // Free video check state
  const [videoUrl, setVideoUrl] = useState('');
  const [checkEmail, setCheckEmail] = useState('');
  const [checkStep, setCheckStep] = useState<'url' | 'email' | 'success' | 'error'>('url');
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkError, setCheckError] = useState('');

  const isValidUrl = (url: string) => {
    try { const p = new URL(url); return ['http:', 'https:'].includes(p.protocol); } catch { return false; }
  };
  const isValidEmail = (em: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em);

  const handleUrlContinue = () => {
    if (!videoUrl.trim()) { setCheckError('Please paste a video URL'); return; }
    if (!isValidUrl(videoUrl.trim())) { setCheckError('Please enter a valid URL'); return; }
    setCheckError(''); setCheckStep('email');
    trackEvent('InitiateCheckout', { content_name: 'Video URL Submitted' });
  };

  const handleSubmitCheck = async () => {
    if (!checkEmail.trim() || !isValidEmail(checkEmail.trim())) {
      setCheckError('Please enter a valid email address'); return;
    }
    setCheckError(''); setCheckLoading(true);
    try {
      const res = await fetch(`${API_ROOT}/free-video-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ video_url: videoUrl.trim(), email: checkEmail.trim().toLowerCase() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).detail || 'Something went wrong. Please try again.');
      }
      setCheckStep('success');
      trackEvent('Lead', { content_name: 'Free Video Check Submitted' });
    } catch (err: any) {
      setCheckError(err.message || 'Failed to submit. Please try again.');
      setCheckStep('error');
    } finally { setCheckLoading(false); }
  };

  const scrollToSection = (id: string) => {
    setShowMenu(false);
    setTimeout(() => {
      const el = document.getElementById(id);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  /* ---------- data (identical to desktop auth.tsx) ---------- */

  const benefits = [
    {
      title: 'Get Hours Back Every Week.',
      description:
        'Your team spends hours every week on revision emails. BrandComply lets creators self-check their videos against your brief before submitting — so most issues are fixed before you ever hit play.',
      icon: <RefreshCcw size={28} style={{ color: PALETTE.gunmetal }} />,
    },
    {
      title: 'Same Brief. Same Standards. Every Creator.',
      description:
        'Whether you\'re working with 5 creators or 50 — every video gets checked against the same briefing, the same way. No subjective reviews, no inconsistency between reviewers. Creators keep their style, you keep your standards.',
      icon: <Target size={28} style={{ color: PALETTE.gunmetal }} />,
    },
    {
      title: 'Nothing Goes Live Off-Brief.',
      description:
        'Missing ad or sponsorship disclosures, wrong CTAs, forgotten talking points — BrandComply catches them before your client ever sees the video. Every submission gets a compliance score so your team knows exactly what needs attention.',
      icon: <Shield size={28} style={{ color: PALETTE.gunmetal }} />,
    },
  ];

  const howItWorks = [
    {
      title: 'Define Brief & Create Submission Link',
      description:
        "Upload your campaign brief. Our AI structures it for you — edit until it's exactly right, then generate a shareable submission link.",
    },
    {
      title: 'Share Submission Link',
      description:
        'You share the submission link with your creators via your preferred communication channel.',
    },
    {
      title: 'Creators Self-Check',
      description:
        "Creators upload their video and get instant, private feedback on how it matches your brief. They can revise and re-check until they're confident — before you ever see it.",
    },
    {
      title: 'Review & Approve Submissions',
      description:
        'View all submissions in one dashboard and see, via the BrandComply score, how well each video matches the brief. So you can focus on creative feedback and request revisions only when needed.',
    },
  ];

  const pricingPlans = [
    {
      name: 'Starter',
      price: '€29',
      subtitle: 'For freelancers & small marketing teams',
      features: ['30 video checks per month', 'Unlimited campaigns', 'Unlimited shareable submission links'],
      highlight: false,
      cta: 'Get Started',
    },
    {
      name: 'Pro',
      price: '€69',
      subtitle: 'For growing agencies & mid-sized marketing teams',
      badge: 'Best Value',
      features: ['100 video checks per month', 'Unlimited campaigns', 'Unlimited shareable submission links', 'Guided onboarding'],
      highlight: true,
      cta: 'Get Started',
    },
    {
      name: 'Custom',
      price: 'Contact us',
      subtitle: 'For large agencies & enterprise brands',
      features: ['Custom built packages', 'Custom branding'],
      highlight: false,
      cta: 'Talk to us',
      href: 'mailto:info@brandcomply.ai',
    },
  ];

  const faqs = [
    {
      question: 'What is BrandComply?',
      answer:
        'BrandComply is a tool that helps marketing agencies and marketing teams to streamline content approvals for UGC and Influencer video marketing campaigns. It enables UGC creators and influencers to self-check their video drafts against campaign briefs before submitting, helping them deliver compliant content faster.',
    },
    {
      question: 'How does BrandComply work?',
      answer:
        'BrandComply uses AI to let influencers and UGC creators receive instant and private feedback on how their videos match the campaign brief before submitting. So they can then re-check their video until they are ready to submit. Your team can then review the submissions in one dashboard and see, via the BrandComply score, how well each video matches the brief.',
    },
    {
      question: 'How do creators self-check their videos against the brief?',
      answer:
        'Our AI learns the requirements of the brief. When a creator uploads a video via the submission link our AI checks how well the video meets these requirements. The creators receive instant objective and private feedback on how their video meets the brief. They can re-check their video until they are ready to submit.',
    },
    {
      question: 'Does BrandComply evaluate the creativity or style of the creator\'s video?',
      answer:
        'No, BrandComply does not evaluate the creativity or style of the creator\'s video. Our system is objective. Your creativity, storytelling, and style are left entirely to the human review of the agency/brand.',
    },
    {
      question: 'Can BrandComply check if advertising content is correctly labeled and disclosed in the video?',
      answer:
        'Yes, BrandComply can check if advertising content is correctly labeled and disclosed in the video if you configure your campaign brief accordingly.',
    },
    {
      question: 'Does BrandComply guarantee legal compliance?',
      answer:
        'No, BrandComply does not guarantee legal compliance. While BrandComply can check videos for certain legal requirements such as disclosures or text overlays, we do not guarantee legal compliance.',
    },
    {
      question: 'Who is BrandComply for?',
      answer:
        'BrandComply is for marketing agencies and marketing teams who want to streamline content approvals for UGC and Influencer video marketing campaigns, saving time and effort, increasing brand safety and legal compliance, and increasing consistency across UGC and Influencer campaigns.',
    },
    {
      question: 'What video file formats do you support?',
      answer:
        'We support the following video format MIME types: video/mp4, video/mpeg, video/mov, video/avi, video/x-flv, video/mpg, video/webm, video/wmv, video/3gpp.',
    },
    {
      question: 'What is the maximum size of a video I can upload?',
      answer: 'The maximum size of a video is 500MB.',
    },
    {
      question: 'What is the maximum length of a video I can upload?',
      answer:
        'There is no maximum length for videos. However, larger files may take longer to analyze.',
    },
    {
      question: 'How many videos can I upload at once?',
      answer: 'As of now, you can upload 1 video at once.',
    },
    {
      question: 'What video languages do you support?',
      answer:
        'We support the following core languages: English, Spanish, Portuguese, French, German, Italian, Dutch, Russian, Polish, Swedish, Danish, Norwegian, Finnish, Greek, Czech, Japanese, Korean, Chinese (Simplified/Traditional), Hindi, Indonesian, Thai, Vietnamese, Turkish, Arabic, Hebrew. And many more.',
    },
    {
      question: 'How long does a video check take?',
      answer:
        'Most video checks complete within 60 seconds, with larger files taking up to 90 seconds.',
    },
    {
      question: 'What counts as a video check?',
      answer:
        'A video check is counted each time a creator uploads a video and BrandComply checks it against your campaign brief. If a creator re-uploads a video, that upload is checked again and counts as an additional video check. The dashboard shows only videos that are currently pending or have been finally submitted, so the number of video checks used can be higher than the number of videos visible in the dashboard.',
    },
    {
      question: 'What happens when I reach my limit?',
      answer:
        "Once you've used all your video checks for the month, new video submissions will be paused until your next billing cycle begins or you upgrade your plan. If you need more than 100 video checks, please contact us at info@brandcomply.ai.",
    },
    {
      question: 'Do I need a credit card to start?',
      answer:
        'Yes, you need a credit card to start.',
    },
    {
      question: 'Can I cancel my plan anytime?',
      answer:
        "Yes! You can cancel your plan at any time through the billing portal. You'll continue to have access until the end of your current billing period.",
    },
    {
      question: 'Is BrandComply available as an API?',
      answer:
        'Yes, BrandComply is available as an API. Please contact us via mail (info@brandcomply.ai) if you are interested in using BrandComply as an API.',
    },
  ];

  /* ---------------------------------------------------------------- */
  /*  RENDER                                                           */
  /* ---------------------------------------------------------------- */
  return (
    <>
      <SEO
        title="AI-powered content approval for Influencer and UGC video campaigns."
        description="BrandComply lets creators self-check their videos against your campaign brief before submission. Launch influencer and UGC campaigns faster, more consistently, and on-brief."
        path="/"
      />
      <div
        style={{
          background: PALETTE.brightSnow,
          minHeight: '100vh',
          overflowX: 'hidden',
          width: '100%',
          maxWidth: '100vw',
        }}
      >
        {/* ======================== STICKY HEADER ======================== */}
        <header
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            background: PALETTE.gunmetal,
            padding: '12px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <img src="/brandcomply_logo.png" alt="BrandComply" style={{ height: 24, width: 'auto' }} />
            <span style={{ fontSize: 16, fontWeight: 700, color: PALETTE.inchworm }}>BrandComply</span>
          </div>
          <button
            onClick={() => setShowMenu(!showMenu)}
            style={{
              background: 'none',
              border: 'none',
              color: PALETTE.inchworm,
              cursor: 'pointer',
              padding: 4,
            }}
            aria-label="Toggle menu"
          >
            {showMenu ? <X size={26} /> : <Menu size={26} />}
          </button>
        </header>

        {/* ======================== MOBILE NAV DRAWER ======================== */}
        {showMenu && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 99,
              background: 'rgba(0,0,0,0.4)',
            }}
            onClick={() => setShowMenu(false)}
          >
            <nav
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '75%',
                maxWidth: 300,
                height: '100%',
                background: PALETTE.gunmetal,
                padding: '72px 24px 32px',
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
                animation: 'mobileSlideIn 0.25s ease-out',
                overflowY: 'auto',
              }}
              onClick={e => e.stopPropagation()}
            >
              {/* Navigation Links */}
              {[
                { label: 'How it Works', action: () => scrollToSection('how-it-works') },
                { label: 'Pricing', action: () => scrollToSection('pricing') },
                { label: 'API', action: () => { setShowMenu(false); navigate('/api'); } },
              ].map((item, i) => (
                <button
                  key={i}
                  onClick={item.action}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    fontSize: 17,
                    fontWeight: 600,
                    textAlign: 'left',
                    padding: '16px 0',
                    cursor: 'pointer',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  {item.label}
                </button>
              ))}

              {/* Auth Buttons */}
              <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <button
                  onClick={() => {
                    setShowMenu(false);
                    setShowAuthModal(true);
                  }}
                  style={{
                    background: 'transparent',
                    border: `2px solid ${PALETTE.brightSnow}`,
                    color: PALETTE.brightSnow,
                    borderRadius: 999,
                    padding: '12px 0',
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: 'pointer',
                    textAlign: 'center',
                  }}
                >
                  Sign In
                </button>
                <button
                  onClick={() => {
                    setShowMenu(false);
                    setShowAuthModal(true);
                  }}
                  style={{
                    background: PALETTE.inchworm,
                    border: 'none',
                    color: PALETTE.gunmetal,
                    borderRadius: 999,
                    padding: '12px 0',
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: 'pointer',
                    textAlign: 'center',
                  }}
                >
                  Sign Up
                </button>
              </div>
            </nav>
          </div>
        )}

        {/* ======================== HERO ======================== */}
        <section
          style={{
            background: PALETTE.gunmetal,
            color: 'white',
            padding: '40px 20px 48px',
          }}
        >
          <h1 style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.25, marginBottom: 16 }}>
            Every creator video.
            <br />
            <span style={{ color: PALETTE.inchworm }}>On-Brief.</span>
            <br />
            Before it reaches your team.
          </h1>

          <p style={{ fontSize: 15, lineHeight: 1.6, opacity: 0.85, marginBottom: 24 }}>
            Creators self-check their videos against your brief before submission.
          </p>

          {/* --- Free Video Check Card --- */}
          <div
            className="free-check-card-mobile"
            style={{
              background: PALETTE.gunmetal,
              border: `1.5px solid ${PALETTE.inchworm}`,
              borderRadius: 16,
              padding: 18,
            }}
          >
            <h3 style={{ fontSize: 18, fontWeight: 700, color: PALETTE.inchworm, marginBottom: 4 }}>
              Paste a creator video. <br /> See what's off-brief.
            </h3>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 14, lineHeight: 1.5 }}>
              Paste a public video URL — we'll check it against a best-practice brief and email you the results.
            </p>

            {checkStep === 'success' ? (
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    background: PALETTE.inchworm,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 12px',
                  }}
                >
                  <Check size={22} style={{ color: PALETTE.gunmetal }} strokeWidth={3} />
                </div>
                <h4 style={{ fontSize: 16, fontWeight: 700, color: PALETTE.brightSnow, marginBottom: 4 }}>
                  We're on it!
                </h4>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>
                  Results will be sent to <strong style={{ color: PALETTE.inchworm }}>{checkEmail}</strong>.
                </p>
                <button
                  onClick={() => { setVideoUrl(''); setCheckEmail(''); setCheckStep('url'); setCheckError(''); }}
                  style={{ marginTop: 12, background: 'none', border: 'none', color: PALETTE.inchworm, fontSize: 12, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Check another video
                </button>
              </div>
            ) : (
              <>
                {/* URL Input */}
                <div style={{ marginBottom: 10 }}>
                  <input
                    type="url"
                    placeholder="Paste a TikTok, Instagram, YouTube URL…"
                    value={videoUrl}
                    onChange={(e) => { setVideoUrl(e.target.value); setCheckError(''); }}
                    disabled={checkStep === 'email'}
                    style={{
                      width: '100%',
                      padding: '11px 14px',
                      borderRadius: 12,
                      border: '1px solid rgba(255,255,255,0.12)',
                      background: checkStep === 'email' ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.08)',
                      color: '#fff',
                      fontSize: 14,
                      outline: 'none',
                      boxSizing: 'border-box',
                      opacity: checkStep === 'email' ? 0.6 : 1,
                    }}
                  />
                  {checkStep === 'email' && (
                    <button
                      onClick={() => { setCheckStep('url'); setCheckError(''); }}
                      style={{ background: 'none', border: 'none', color: PALETTE.inchworm, fontSize: 11, cursor: 'pointer', marginTop: 3 }}
                    >
                      Change URL
                    </button>
                  )}
                </div>

                {/* Video Preview */}
                {videoUrl.trim() && (() => {
                  try {
                    const parsed = new URL(videoUrl.trim());
                    if (!['http:', 'https:'].includes(parsed.protocol)) return null;
                    const host = parsed.hostname.replace('www.', '');
                    let platform = host;
                    let thumbnail: string | null = null;

                    if (host.includes('youtube.com') || host.includes('youtu.be')) {
                      platform = 'YouTube';
                      let vid = '';
                      if (host.includes('youtu.be')) vid = parsed.pathname.slice(1);
                      else { vid = parsed.searchParams.get('v') || ''; if (!vid) { const m = parsed.pathname.match(/\/shorts\/([^/?]+)/); if (m) vid = m[1]; } }
                      if (vid) thumbnail = `https://img.youtube.com/vi/${vid}/mqdefault.jpg`;
                    }
                    else if (host.includes('tiktok.com')) platform = 'TikTok';
                    else if (host.includes('instagram.com')) platform = 'Instagram';
                    else if (host.includes('vimeo.com')) platform = 'Vimeo';
                    else if (host.includes('facebook.com') || host.includes('fb.watch')) platform = 'Facebook';
                    else if (host.includes('twitter.com') || host.includes('x.com')) platform = 'X / Twitter';

                    return (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.07)',
                          borderRadius: 10,
                          padding: thumbnail ? 0 : '8px 10px',
                          marginBottom: 10,
                          overflow: 'hidden',
                        }}
                      >
                        {thumbnail ? (
                          <>
                            <img src={thumbnail} alt="" style={{ width: 72, height: 40, objectFit: 'cover', borderRadius: '10px 0 0 10px', flexShrink: 0 }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                            <div style={{ padding: '6px 10px 6px 0', minWidth: 0 }}>
                              <p style={{ fontSize: 12, fontWeight: 600, color: PALETTE.inchworm, margin: 0 }}>{platform}</p>
                              <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{videoUrl.trim()}</p>
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <ArrowRight size={14} style={{ color: PALETTE.inchworm }} />
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <p style={{ fontSize: 12, fontWeight: 600, color: PALETTE.inchworm, margin: 0 }}>{platform}</p>
                              <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{videoUrl.trim()}</p>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  } catch { return null; }
                })()}

                {/* Email Input */}
                {checkStep === 'email' && (
                  <div style={{ marginBottom: 10 }}>
                    <input
                      type="email"
                      placeholder="Your email address"
                      value={checkEmail}
                      onChange={(e) => { setCheckEmail(e.target.value); setCheckError(''); }}
                      style={{
                        width: '100%',
                        padding: '11px 14px',
                        borderRadius: 12,
                        border: '1px solid rgba(255,255,255,0.12)',
                        background: 'rgba(255,255,255,0.08)',
                        color: '#fff',
                        fontSize: 14,
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                )}

                {/* Error */}
                {(checkError || checkStep === 'error') && (
                  <div
                    style={{
                      fontSize: 12,
                      textAlign: 'center',
                      padding: 8,
                      borderRadius: 10,
                      marginBottom: 10,
                      color: '#FE7733',
                      background: 'rgba(254,119,51,0.1)',
                      border: '1px solid rgba(254,119,51,0.2)',
                    }}
                  >
                    {checkError}
                    {checkStep === 'error' && (
                      <button
                        onClick={() => { setCheckStep('email'); setCheckError(''); }}
                        style={{ display: 'block', margin: '4px auto 0', background: 'none', border: 'none', color: '#FE7733', fontSize: 11, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        Try again
                      </button>
                    )}
                  </div>
                )}

                {/* CTA */}
                <button
                  onClick={checkStep === 'url' ? handleUrlContinue : handleSubmitCheck}
                  disabled={checkLoading}
                  style={{
                    width: '100%',
                    padding: '12px 0',
                    borderRadius: 999,
                    border: 'none',
                    background: checkLoading ? PALETTE.americanSilver : PALETTE.inchworm,
                    color: PALETTE.gunmetal,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: checkLoading ? 'default' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  {checkLoading ? (
                    <><Loader2 size={15} className="mobile-spinner" style={{ width: 15, height: 15 }} /> Submitting...</>
                  ) : checkStep === 'url' ? (
                    <>Continue <ArrowRight size={15} /></>
                  ) : (
                    <>Analyze & Send Results <ArrowRight size={15} /></>
                  )}
                </button>
              </>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
            <button
              onClick={openAuth}
              style={{
                background: 'transparent',
                color: PALETTE.inchworm,
                border: `2px solid ${PALETTE.inchworm}`,
                borderRadius: 999,
                padding: '12px 28px',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
                minWidth: 140,
              }}
            >
              Start Free Trial
            </button>
          </div>
        </section>

        {/* ==================== BENEFITS ==================== */}
        <section style={{ padding: '48px 20px', background: PALETTE.brightSnow }}>
          <h2
            style={{
              fontSize: 24,
              fontWeight: 700,
              textAlign: 'center',
              marginBottom: 32,
              color: PALETTE.gunmetal,
              lineHeight: 1.3,
            }}
          >
            Your team reviews less. Your creators submit better.
            <br />
            Every time.
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {benefits.map((b, i) => (
              <div
                key={i}
                style={{
                  background: 'white',
                  borderRadius: 20,
                  padding: 20,
                  border: '1px solid #f0f0f0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 14,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: PALETTE.inchworm,
                    marginBottom: 14,
                  }}
                >
                  {b.icon}
                </div>
                <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6, color: PALETTE.gunmetal }}>
                  {b.title}
                </h3>
                <p style={{ fontSize: 14, lineHeight: 1.6, color: `${PALETTE.gunmetal}bb`, margin: 0 }}>
                  {b.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* ================= HOW IT WORKS ================= */}
        <section id="how-it-works" style={{ padding: '48px 20px', background: PALETTE.brightSnow }}>
          <h2
            style={{
              fontSize: 24,
              fontWeight: 700,
              textAlign: 'center',
              marginBottom: 36,
              color: PALETTE.gunmetal,
              lineHeight: 1.3,
            }}
          >
            From brief to approved —
            <br />
            without the back-and-forth.
          </h2>

          <div style={{ position: 'relative', paddingLeft: 28 }}>
            {/* vertical line */}
            <div
              style={{
                position: 'absolute',
                left: 7,
                top: 0,
                bottom: 0,
                width: 2,
                background: `${PALETTE.americanSilver}40`,
              }}
            />

            {howItWorks.map((step, i) => (
              <div key={i} style={{ position: 'relative', marginBottom: i < howItWorks.length - 1 ? 28 : 0 }}>
                <div
                  style={{
                    position: 'absolute',
                    left: -23,
                    top: 4,
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    background: PALETTE.inchworm,
                    zIndex: 1,
                  }}
                />
                <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: PALETTE.gunmetal }}>
                  {step.title}
                </h3>
                <p style={{ fontSize: 14, lineHeight: 1.6, color: `${PALETTE.gunmetal}aa`, margin: 0 }}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* ==================== PRICING ==================== */}
        <section id="pricing" style={{ padding: '48px 20px', background: PALETTE.brightSnow }}>
          <h2
            style={{
              fontSize: 24,
              fontWeight: 700,
              textAlign: 'center',
              marginBottom: 32,
              color: PALETTE.gunmetal,
            }}
          >
            Flexible Plans for Every Stage.
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {pricingPlans.map((plan, i) => (
              <div
                key={i}
                style={{
                  background: 'white',
                  borderRadius: 20,
                  padding: 24,
                  border: `2px solid ${plan.highlight ? PALETTE.inchworm : '#f0f0f0'}`,
                  position: 'relative',
                }}
              >
                {plan.badge && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 14,
                      right: 14,
                      background: PALETTE.inchworm,
                      color: PALETTE.gunmetal,
                      padding: '3px 10px',
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    {plan.badge}
                  </div>
                )}

                <h3 style={{ fontSize: 22, fontWeight: 700, color: PALETTE.gunmetal, marginBottom: 8 }}>
                  {plan.name}
                </h3>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
                    <span style={{ fontSize: 36, fontWeight: 700, color: PALETTE.gunmetal }}>{plan.price}</span>
                    {plan.price !== 'Contact us' && (
                      <span style={{ fontSize: 15, color: `${PALETTE.gunmetal}60` }}>/month</span>
                    )}
                  </div>
                  <p style={{ fontSize: 13, color: `${PALETTE.gunmetal}70`, margin: 0 }}>{plan.subtitle}</p>
                </div>

                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px' }}>
                  {plan.features.map((f, fi) => (
                    <li key={fi} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                      <div
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          background: PALETTE.inchworm,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          marginTop: 2,
                        }}
                      >
                        <Check size={11} style={{ color: PALETTE.gunmetal }} strokeWidth={3} />
                      </div>
                      <span style={{ fontSize: 14, color: PALETTE.gunmetal }}>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => (plan.href ? (window.location.href = plan.href) : openAuth())}
                  style={{
                    width: '100%',
                    padding: '12px 0',
                    borderRadius: 999,
                    border: 'none',
                    background: PALETTE.inchworm,
                    color: PALETTE.gunmetal,
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                  }}
                >
                  {plan.cta}
                  <ExternalLink size={14} />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* ====================== FAQ ====================== */}
        <section id="faq" style={{ padding: '48px 20px', background: PALETTE.brightSnow }}>
          <h2
            style={{
              fontSize: 24,
              fontWeight: 700,
              textAlign: 'center',
              marginBottom: 28,
              color: PALETTE.gunmetal,
            }}
          >
            Frequently asked questions
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {faqs.map((faq, i) => (
              <div
                key={i}
                style={{
                  background: 'white',
                  borderRadius: 12,
                  border: `1px solid ${PALETTE.americanSilver}30`,
                  overflow: 'hidden',
                }}
              >
                <button
                  onClick={() => toggleFaq(i)}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    border: 'none',
                    background: 'transparent',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    gap: 10,
                  }}
                >
                  <span style={{ fontSize: 14, fontWeight: 600, color: PALETTE.gunmetal }}>{faq.question}</span>
                  <ChevronDown
                    size={18}
                    style={{
                      color: PALETTE.gunmetal,
                      flexShrink: 0,
                      transform: openFaqItems[i] ? 'rotate(180deg)' : 'rotate(0)',
                      transition: 'transform 0.2s',
                    }}
                  />
                </button>
                {openFaqItems[i] && (
                  <div style={{ padding: '0 16px 14px' }}>
                    <p style={{ fontSize: 13, lineHeight: 1.6, color: `${PALETTE.gunmetal}aa`, margin: 0 }}>
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* ================== FINAL CTA ================== */}
        <section
          style={{
            padding: '48px 20px',
            background: `linear-gradient(to bottom right, ${PALETTE.gunmetal}, ${PALETTE.gunmetal}dd)`,
            textAlign: 'center',
          }}
        >
          <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 10, color: 'white', lineHeight: 1.3 }}>
            Your next creator campaign — without the revision chaos.
          </h2>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.65)', marginBottom: 24, lineHeight: 1.5 }}>
            Still not sure? Paste a video URL above and see BrandComply in action.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <button
              onClick={openAuth}
              style={{
                background: PALETTE.inchworm,
                color: PALETTE.gunmetal,
                border: 'none',
                borderRadius: 999,
                padding: '14px 0',
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
                width: '100%',
                maxWidth: 280,
              }}
            >
              Start Free Trial
            </button>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              style={{
                background: 'transparent',
                color: PALETTE.brightSnow,
                border: `2px solid ${PALETTE.brightSnow}`,
                borderRadius: 999,
                padding: '14px 0',
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
                width: '100%',
                maxWidth: 280,
              }}
            >
              Try it free
            </button>
          </div>
        </section>

        {/* =================== FOOTER =================== */}
        <footer className="py-12 text-white" style={{ background: PALETTE.gunmetal }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row items-start gap-8 md:gap-16">
              {/* Brand Info */}
              <div className="max-w-xs">
                <div
                  className="flex items-center space-x-1 cursor-pointer"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  <img src="/brandcomply_logo.png" alt="Brand Logo" className="h-6 w-auto" />
                  <div className="text-xl font-bold" style={{ color: PALETTE.inchworm }}>
                    BrandComply
                  </div>
                </div>
                <p className="text-white/70 mt-4">
                  AI-powered content approval for Influencer and UGC video campaigns.  
                </p>
              </div>

              {/* Product */}
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: PALETTE.inchworm }}>
                  Product
                </h3>
                <div className="space-y-3">
                  <button
                    onClick={() => scrollToSection('how-it-works')}
                    className="block text-white/70 hover:text-white transition-colors text-left"
                  >
                    How it Works
                  </button>
                  <button
                    onClick={() => scrollToSection('pricing')}
                    className="block text-white/70 hover:text-white transition-colors text-left"
                  >
                    Pricing
                  </button>
                  <button
                    onClick={() => navigate('/api')}
                    className="block text-white/70 hover:text-white transition-colors text-left"
                  >
                    API
                  </button>
                </div>
              </div>

              {/* Legal */}
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: PALETTE.inchworm }}>
                  Legal
                </h3>
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/terms-of-service')}
                    className="block text-white/70 hover:text-white transition-colors text-left"
                  >
                    Terms of Service
                  </button>
                  <button
                    onClick={() => navigate('/privacy-policy')}
                    className="block text-white/70 hover:text-white transition-colors text-left"
                  >
                    Privacy Policy
                  </button>
                  <button
                    onClick={() => navigate('/imprint')}
                    className="block text-white/70 hover:text-white transition-colors text-left"
                  >
                    Imprint
                  </button>
                </div>
              </div>
            </div>

            <div className="border-t border-white/20 mt-12 pt-8 text-center text-white/70">
              <p>&copy; 2026 BrandComply. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>

      {/* =================== AUTH MODAL =================== */}
      {showAuthModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 9999,
            animation: 'mobileFadeIn 0.2s ease-in-out',
          }}
          onClick={() => setShowAuthModal(false)}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              background: 'white',
              overflowY: 'auto',
              overflowX: 'hidden',
              animation: 'mobileSlideIn 0.3s ease-out',
            }}
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setShowAuthModal(false)}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                background: 'rgba(0,0,0,0.06)',
                border: 'none',
                fontSize: 22,
                cursor: 'pointer',
                zIndex: 10,
                width: 36,
                height: 36,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#444',
              }}
            >
              &times;
            </button>
            <AuthMobile />
          </div>
        </div>
      )}

      {/* Back to top */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Back to top"
        style={{
          position: 'fixed',
          bottom: 24,
          right: 20,
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: PALETTE.gunmetal,
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          opacity: showBackToTop ? 0.85 : 0,
          pointerEvents: showBackToTop ? 'auto' : 'none',
          transition: 'opacity 0.3s ease',
          zIndex: 90,
        }}
      >
        <ChevronUp size={20} color={PALETTE.inchworm} />
      </button>

      <style>{`
        @keyframes mobileFadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes mobileSlideIn { from { transform:translateX(100%) } to { transform:translateX(0) } }
        @keyframes glow-breathe-mobile {
          0%, 100% { box-shadow: 0 0 12px 2px rgba(177,250,99,0.10), 0 0 4px 0px rgba(177,250,99,0.08); }
          50% { box-shadow: 0 0 28px 6px rgba(177,250,99,0.22), 0 0 10px 2px rgba(177,250,99,0.14); }
        }
        .free-check-card-mobile {
          animation: glow-breathe-mobile 3.5s ease-in-out infinite;
        }
      `}</style>
    </>
  );
};

export default LandingMobile;
